import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Spinner } from '@spark';

import { useAuth } from '@/features/auth/AuthContext.jsx';

import { deriveCompanyFromEmail } from '../utils/companyFromEmail';
import CreateWorkspaceWizard, { ONBOARDING_STEPS, completedStepFor } from './CreateWorkspaceWizard';
import OnboardingLayout from './OnboardingLayout';

const goHome = () => {
    window.location.href = '/';
};

/**
 * The steps this user walks through.  Someone who only joined workspaces
 * they were invited to (none owned) never sees "Define your workspace" —
 * the owner already configured it (Figma 9224:4435 is the owner's screen).
 * Someone with no workspace at all, or who owns one, keeps the full list so
 * a half-finished creation can resume on that step.
 */
export function visibleOnboardingSteps(workspaces) {
    const list = Array.isArray(workspaces) ? workspaces : [];
    const hideWorkspaceStep = list.length > 0 && !list.some((ws) => ws?.is_owner);
    return hideWorkspaceStep ? ONBOARDING_STEPS.filter((step) => step.id !== 'workspace') : ONBOARDING_STEPS;
}

/**
 * Pick the wizard step (index into `steps`) a returning user lands on.
 *
 * `completed_step` is stored with the global numbering (about 1, workspace 2,
 * improve 3, audience 4, terms 5) regardless of which steps are visible, so
 * the landing step is the first visible step whose number is above it.  The
 * one correction is a user whose profile says the workspace step is done
 * while no workspace exists (the `POST /workspaces` call failed after the
 * profile was saved) — they are sent back to "Define your workspace".  The
 * last step is the terms screen, which is where anyone who finished
 * everything but the terms resumes.
 */
export function resolveInitialStep(onboarding, hasWorkspace, steps = ONBOARDING_STEPS) {
    const raw = Number(onboarding?.completed_step);
    let completed = Number.isFinite(raw) ? Math.max(Math.trunc(raw), 0) : 0;
    const workspaceStep = completedStepFor('workspace');
    if (completed >= workspaceStep && !hasWorkspace && steps.some((step) => step.id === 'workspace')) {
        completed = workspaceStep - 1;
    }
    const index = steps.findIndex((step) => completedStepFor(step.id) > completed);
    return index === -1 ? steps.length - 1 : index;
}

/**
 * The workspace the wizard treats as "already defined": the one the user
 * owns (created on the workspace step) over any they were only invited to.
 */
export function pickExistingWorkspace(workspaces) {
    if (!Array.isArray(workspaces) || !workspaces.length) return null;
    return workspaces.find((ws) => ws?.is_owner) || workspaces[0];
}

/**
 * `/create-workspace` — the onboarding wizard every new (non-god) account
 * completes once (Figma "MVP-DESIGNES" onboarding screens).  `AuthContext`
 * sends the user here when `POST /users/bootstrap` answers
 * `state: "no_workspace"` with `platform_role: "creator"`, or
 * `state: "onboarding"` for any account that has not accepted the terms yet
 * (including invitees who already belong to a workspace).  Gods go to
 * `/admin` and waiting-list applicants to `/waitlist/apply` instead, so no
 * role check is repeated here.
 *
 * Rendered as a standalone full-page route (outside the sidebar shell) on
 * `OnboardingLayout`.  Each step is persisted on the user document, so the
 * wizard resumes at `user.onboarding.completed_step`.  "Continue to Specific"
 * reloads to `/` so the session cookie (now carrying `workspace_id`) is
 * picked up by the bootstrap.
 */
export default function CreateWorkspacePage({ onCreated = goHome }) {
    const navigate = useNavigate();
    const { user, loading, isAuthEnabled, workspaces } = useAuth();

    useEffect(() => {
        if (loading) return;
        if (isAuthEnabled && !user) navigate('/login', { replace: true });
    }, [loading, isAuthEnabled, user, navigate]);

    if (loading || (isAuthEnabled && !user)) {
        return (
            <OnboardingLayout>
                <div className="onboarding-loading">
                    <Spinner size="large" aria-label="Loading" />
                </div>
            </OnboardingLayout>
        );
    }

    const onboarding = user?.onboarding || null;
    const existingWorkspace = pickExistingWorkspace(workspaces);
    const steps = visibleOnboardingSteps(workspaces);

    return (
        <CreateWorkspaceWizard
            key={user?.email || 'anonymous'}
            steps={steps}
            initialName={user?.name || ''}
            initialCompany={deriveCompanyFromEmail(user?.email)}
            initialCountry={user?.country || ''}
            initialStep={resolveInitialStep(onboarding, Boolean(existingWorkspace), steps)}
            initialGoals={Array.isArray(onboarding?.improve_goals) ? onboarding.improve_goals : []}
            initialAudience={Array.isArray(onboarding?.model_audience) ? onboarding.model_audience : []}
            initialTermsAccepted={Boolean(onboarding?.terms_accepted_at)}
            existingWorkspace={existingWorkspace}
            onCreated={onCreated}
        />
    );
}
